import React, { useEffect, useState } from "react";
import api, { money, fmtDate } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { PageHeader, StatusBadge, Spinner, EmptyState } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Receipt, Plus, Search, RotateCcw, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const Input = (p) => <input {...p} className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 w-full" />;
const Sel = ({ children, ...p }) => <select {...p} className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white outline-none w-full">{children}</select>;
const Label = ({ children }) => <label className="text-sm font-medium text-slate-700 block mb-1">{children}</label>;

export default function Sales() {
  const { store } = useStore();
  const [rows, setRows] = useState(null);
  const [consignors, setConsignors] = useState([]);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [returnLine, setReturnLine] = useState(null);
  const [retAmt, setRetAmt] = useState("");
  const [retReason, setRetReason] = useState("");
  const cur = store?.currency || "USD";

  const emptyLine = { item_id: "", product_name: "", selling_price: "", discount: "", consignor_id: "" };
  const [sale, setSale] = useState({ order_date: "", customer_reference: "", line_items: [{ ...emptyLine }] });

  const load = () => { setRows(null); api.get("/sales", { params: { search } }).then((r) => setRows(r.data)); };
  useEffect(() => {
    api.get("/consignors").then((r) => setConsignors(r.data.filter((c) => c.status === "active")));
    api.get("/items", { params: { status: "active" } }).then((r) => setItems(r.data));
  }, [store?.id]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search, store?.id]);

  const setLine = (i, k, v) => setSale((p) => {
    const li = [...p.line_items]; li[i] = { ...li[i], [k]: v };
    if (k === "item_id" && v) {
      const it = items.find((x) => x.id === v);
      if (it) { li[i].product_name = it.product_name; li[i].selling_price = it.current_price; li[i].consignor_id = it.consignor_id || ""; }
    }
    return { ...p, line_items: li };
  });

  const submit = async () => {
    const valid = sale.line_items.filter((l) => l.product_name && l.selling_price);
    if (!valid.length) return toast.error("Add at least one line item");
    try {
      await api.post("/sales", {
        platform: "manual", order_date: sale.order_date || undefined, customer_reference: sale.customer_reference,
        line_items: valid.map((l) => ({
          item_id: l.item_id || null, product_name: l.product_name, quantity: 1,
          original_price: Number(l.selling_price), selling_price: Number(l.selling_price),
          discount: Number(l.discount || 0), consignor_id: l.consignor_id || null,
        })),
      });
      toast.success("Sale recorded"); setOpen(false);
      setSale({ order_date: "", customer_reference: "", line_items: [{ ...emptyLine }] }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitReturn = async () => {
    if (!retAmt) return toast.error("Refund amount required");
    try {
      await api.post("/returns", { sale_line_item_id: returnLine.id, refund_amount: Number(retAmt), reason: retReason });
      toast.success("Return recorded"); setReturnLine(null); setRetAmt(""); setRetReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader title="Sales" subtitle="Normalized orders — from CSV, manual entry, or connectors">
        <button data-testid="record-sale-btn" onClick={() => setOpen(true)} className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-4 py-2 rounded-lg text-sm">
          <Plus className="w-4 h-4" /> Record Sale
        </button>
      </PageHeader>

      <div className="relative mb-4">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input data-testid="sale-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by order ID…"
          className="pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm w-full max-w-md outline-none focus:border-emerald-500" />
      </div>

      {rows === null ? <Spinner /> : rows.length === 0 ? (
        <EmptyState icon={Receipt} title="No sales yet" description="Record a sale or import via CSV to feed the settlement engine." />
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Order</th><th className="px-4 py-3">Platform</th><th className="px-4 py-3">Date</th>
                <th className="px-4 py-3 text-right">Subtotal</th><th className="px-4 py-3 text-right">Discounts</th>
                <th className="px-4 py-3 text-right">Refunds</th><th className="px-4 py-3 text-right">Net</th><th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((s, i) => (
                <React.Fragment key={s.id}>
                  <tr data-testid={`sale-row-${i}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => setExpanded((p) => ({ ...p, [s.id]: !p[s.id] }))}>
                    <td className="px-4 py-3 font-mono-fin text-xs font-semibold text-slate-900 flex items-center gap-1">
                      {expanded[s.id] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}{s.external_order_id}</td>
                    <td className="px-4 py-3 capitalize text-slate-600">{s.platform}</td>
                    <td className="px-4 py-3 text-slate-500">{fmtDate(s.order_date)}</td>
                    <td className="px-4 py-3 text-right font-mono-fin">{money(s.subtotal, cur)}</td>
                    <td className="px-4 py-3 text-right font-mono-fin text-rose-600">{s.discounts ? `-${money(s.discounts, cur)}` : "—"}</td>
                    <td className="px-4 py-3 text-right font-mono-fin text-rose-600">{s.refund_amount ? `-${money(s.refund_amount, cur)}` : "—"}</td>
                    <td className="px-4 py-3 text-right font-mono-fin font-semibold">{money(s.net_amount, cur)}</td>
                    <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                  </tr>
                  {expanded[s.id] && (
                    <tr><td colSpan={8} className="bg-slate-50/60 px-6 py-3">
                      <div className="space-y-1">
                        {s.line_items.map((l) => (
                          <div key={l.id} className="flex items-center justify-between text-xs bg-white rounded-lg px-3 py-2 border border-slate-100">
                            <span className="font-medium text-slate-700">{l.product_name}</span>
                            <div className="flex items-center gap-4">
                              <span>Sold {money(l.selling_price, cur)}</span>
                              <span className="text-slate-500">Payout {l.consignor_payout_pct}% → <b className="text-amber-700">{money(l.consignor_payout_amount, cur)}</b></span>
                              <button data-testid={`return-line-${l.id}`} onClick={(e) => { e.stopPropagation(); setReturnLine(l); setRetAmt(String(l.selling_price)); }}
                                className="flex items-center gap-1 text-rose-600 hover:underline"><RotateCcw className="w-3 h-3" /> Return</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="record-sale-dialog">
          <DialogHeader><DialogTitle>Record Sale</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Order date</Label><Input data-testid="sale-date-input" type="date" value={sale.order_date} onChange={(e) => setSale({ ...sale, order_date: e.target.value })} /></div>
              <div><Label>Customer reference</Label><Input value={sale.customer_reference} onChange={(e) => setSale({ ...sale, customer_reference: e.target.value })} /></div>
            </div>
            {sale.line_items.map((l, i) => (
              <div key={i} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`sale-line-${i}`}>
                <div><Label>Item (optional — links to inventory)</Label>
                  <Sel data-testid={`sale-line-item-${i}`} value={l.item_id} onChange={(e) => setLine(i, "item_id", e.target.value)}>
                    <option value="">— Manual entry —</option>
                    {items.map((it) => <option key={it.id} value={it.id}>{it.product_name} · {money(it.current_price, cur)}</option>)}
                  </Sel>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Product name</Label><Input value={l.product_name} onChange={(e) => setLine(i, "product_name", e.target.value)} /></div>
                  <div><Label>Consignor</Label>
                    <Sel value={l.consignor_id} onChange={(e) => setLine(i, "consignor_id", e.target.value)}>
                      <option value="">— None —</option>{consignors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </Sel></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Sale price</Label><Input type="number" value={l.selling_price} onChange={(e) => setLine(i, "selling_price", e.target.value)} /></div>
                  <div><Label>Discount</Label><Input type="number" value={l.discount} onChange={(e) => setLine(i, "discount", e.target.value)} /></div>
                </div>
                {sale.line_items.length > 1 && (
                  <button onClick={() => setSale((p) => ({ ...p, line_items: p.line_items.filter((_, x) => x !== i) }))}
                    className="text-xs text-rose-600 flex items-center gap-1"><Trash2 className="w-3 h-3" /> Remove line</button>
                )}
              </div>
            ))}
            <button data-testid="add-line-btn" onClick={() => setSale((p) => ({ ...p, line_items: [...p.line_items, { ...emptyLine }] }))}
              className="text-sm text-emerald-700 font-medium">+ Add line item</button>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm">Cancel</button>
            <button data-testid="submit-sale-btn" onClick={submit} className="px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-semibold text-sm">Record Sale</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!returnLine} onOpenChange={() => setReturnLine(null)}>
        <DialogContent data-testid="return-dialog">
          <DialogHeader><DialogTitle>Record Return / Refund</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">{returnLine?.product_name} — original {money(returnLine?.selling_price, cur)}</p>
            <div><Label>Refund amount</Label><Input data-testid="return-amount-input" type="number" value={retAmt} onChange={(e) => setRetAmt(e.target.value)} /></div>
            <div><Label>Reason</Label><Input data-testid="return-reason-input" value={retReason} onChange={(e) => setRetReason(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setReturnLine(null)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm">Cancel</button>
            <button data-testid="submit-return-btn" onClick={submitReturn} className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-semibold text-sm">Record Return</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
